import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaBuilding, 
  FaEnvelope, 
  FaPhoneAlt, 
  FaCity, 
  FaMailBulk, 
  FaUser, 
  FaPlus, 
  FaArrowLeft,
  FaSave,
  FaSpinner,
  FaMapMarkerAlt 
} from 'react-icons/fa';
import { getSupplier, createSupplier, updateSupplier, getCategories, getProducts } from './supplierService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import InputMask from '../../components/shared/MaskedInput';
import GooglePlaceAutocomplete from '../../components/shared/GooglePlaceAutocomplete';
import CustomSelect from '../../components/shared/CustomSelect';
import TextArea from '../../components/shared/TextArea';
import ProductPriceModal from './ProductPriceModal';
import ProductListDisplay from '../../components/Suppliers/ProductListDisplay';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  SubmitButton
} from '../../components/styled/FormComponents';
import styled from 'styled-components';

const ProductSection = styled(FormSection)`
  .product-header {
    display: block;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing(3)};
  }
`;

const AddProductButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.surface};
  border: 1px dashed ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  justify-content: center;
  margin-top: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};

  &:hover {
    background: ${props => props.theme.colors.primary}11;
    transform: translateY(-1px);
  }

  svg {
    font-size: 1.2rem;
  }
`;

const NotesField = styled(FormField)`
  grid-column: 1 / -1;
`;

const SupplierForm = () => {
  const { id } = useParams();  // Get the supplier ID for edit mode
  const [supplier, setSupplier] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: null,
    longitude: null,
    categories: [],
    products: [],
    notes: '',
  });
  const [categories, setCategories] = useState([]);  // Store available categories
  const [products, setProducts] = useState([]); // Available products for selection
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal open/close
  const [editIndex, setEditIndex] = useState(null); // Track the index for product editing
  const [loadingBtn, setLoadingBtn] = useState(false);
  const navigate = useNavigate();



  // Fetch supplier data for editing
  useEffect(() => {
    if (id) {
      const fetchSupplier = async () => {
        const data = await getSupplier(id);
        
        // Map categories and products to match the API format {id, name}
        const mappedCategories = data.categories.map((cat) => ({
          value: cat.id, label: cat.name
          // id: cat.id, label: cat.name
        }));
        const mappedProducts = data.products.map((prod) => ({
          id: prod.id, // Ensure product 'id' is used for the API
          label: prod.name, // Name of the product
          price: prod.SupplierProduct.unitPrice, // The unit price
          unit: prod.unit // Measurement unit
        }));

        setSupplier({
          ...data,
          categories: mappedCategories,  // Pre-fill selected categories
          products: mappedProducts,      // Pre-fill selected products
        });
      };
      fetchSupplier();
      
    }
  }, [id]);

  // Fetch categories and products from the API
  useEffect(() => {
    const fetchCategories = async () => {
      const data = await getCategories();
      // setCategories(data.map((cat) => ({ id: cat.id, label: cat.name })));  // Map categories for react-select
      setCategories(data.map((cat) => ({ value: cat.id, label: cat.name })));  // Map categories for react-select
    };

    const fetchProducts = async () => {
      const data = await getProducts();
      setProducts(data.map((prod) => ({ value: prod.id, label: prod.name }))); // Map products for react-select
    };


    fetchCategories();
    fetchProducts();
  }, []);

  // Handle address selection from GooglePlaceAutocomplete
  const handleAddressSelect = (address, lat, lng, place) => {
    const addressComponents = place.address_components;

    const city = addressComponents.find((comp) =>
      comp.types.includes('locality')
    )?.long_name || '';

    const state = addressComponents.find((comp) =>
      comp.types.includes('administrative_area_level_1')
    )?.short_name || '';

    const zipCode = addressComponents.find((comp) =>
      comp.types.includes('postal_code')
    )?.long_name || '';

    setSupplier((prevSupplier) => ({
      ...prevSupplier,
      address,
      latitude: lat,
      longitude: lng,
      city,
      state,
      zipCode,
    }));
  };

  // Handle categories and products selection
  const handleTagAdd = (type, selectedOptions) => {
    setSupplier((prevSupplier) => ({
      ...prevSupplier,
      // [type]: selectedOptions.map((option) => ({ id: option.id, name: option.label }))  // Store {id, name}
      [type]: selectedOptions
    }));
  };

 
  // Handle saving the selected product and price
  const handleProductSave = (newProduct) => {
    setSupplier((prevSupplier) => {
      const updatedProducts = [...prevSupplier.products];
      if (editIndex !== null) {
        updatedProducts[editIndex] = newProduct; // Edit existing product
      } else {
        updatedProducts.push(newProduct); // Add new product
      }
      return { ...prevSupplier, products: updatedProducts }; // Update supplier products
    });
    setIsModalOpen(false); // Close modal after save
    setEditIndex(null); // Reset edit index
  };

  // Handle editing a product
  const handleProductEdit = (index) => {
    setEditIndex(index); // Set the index to edit
    setIsModalOpen(true); // Open modal
  };

  // Handle deleting a product
  const handleProductDelete = (index) => {
    setSupplier((prevSupplier) => {
      const updatedProducts = [...prevSupplier.products];
      updatedProducts.splice(index, 1); // Remove product at index
      return { ...prevSupplier, products: updatedProducts };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingBtn(true);
    try{
    const formattedSupplier = {
      ...supplier,
      categories: supplier.categories.map((category) => ({
        id: category.value,    // Transform value to id
        label: category.label  // Keep the label as it is
      })),

      products: supplier.products.map((product) => ({
        id: product.id,     // Transform value to id
        label: product.label,   // Keep the label as it is
        unitPrice: product.price,
        unit: product.unit
      })),
    };

    if (id) {
      await updateSupplier(id, formattedSupplier);  // Update supplier if in edit mode
    } else {
      await createSupplier(formattedSupplier);      // Create new supplier
    }
    navigate('/suppliers');
  }catch (error){
    console.error('Error saving supplier: ', error);
  }finally{
    setLoadingBtn(false);
  }
  };

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/suppliers')}>
            <FaArrowLeft /> Back to Suppliers
          </BackButton>
          <h2>{id ? 'Edit Supplier' : 'New Supplier'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Basic Information</h3>
            <FormGrid>
              <FormField index={0}>
                <Input 
                  label="Supplier Name"
                  icon={<FaBuilding />} 
                  value={supplier.name} 
                  onChange={(e) => setSupplier({ ...supplier, name: e.target.value })} 
                  placeholder="Enter supplier name"
                  required
                />
              </FormField>

              <FormField index={1}>
                <Input 
                  label="Contact Person"
                  icon={<FaUser />} 
                  value={supplier.contactPerson} 
                  onChange={(e) => setSupplier({ ...supplier, contactPerson: e.target.value })} 
                  placeholder="Enter contact person"
                />
              </FormField>

              <FormField index={2}>
                <Input 
                  label="Email"
                  icon={<FaEnvelope />} 
                  type="email"
                  value={supplier.email} 
                  onChange={(e) => setSupplier({ ...supplier, email: e.target.value })} 
                  placeholder="Enter email address"
                />
              </FormField>

              <FormField index={3}>
                <InputMask
                  label="Phone Number"
                  icon={<FaPhoneAlt />}
                  mask="(999) 999-9999"
                  value={supplier.phone}
                  onChange={(e) => setSupplier({ ...supplier, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>Address Information</h3>
            <FormGrid>
              <FormField index={4}>
                <GooglePlaceAutocomplete 
                  label="Address"
                  icon={<FaMapMarkerAlt />}
                  onSelect={handleAddressSelect} 
                  initialValue={supplier.address}
                  placeholder="Start typing address"
                  required
                />
              </FormField>

              <FormField index={5}>
                <Input 
                  label="City"
                  icon={<FaCity />}
                  value={supplier.city}
                  onChange={(e) => setSupplier({ ...supplier, city: e.target.value })}
                  placeholder="City"
                  required
                />
              </FormField>

              <FormField index={6}>
                <Input 
                  label="State"
                  icon={<FaCity />}
                  value={supplier.state}
                  onChange={(e) => setSupplier({ ...supplier, state: e.target.value })}
                  placeholder="State"
                  required
                />
              </FormField>

              <FormField index={7}>
                <Input 
                  label="Zip Code"
                  icon={<FaMailBulk />}
                  value={supplier.zipCode}
                  onChange={(e) => setSupplier({ ...supplier, zipcode: e.target.value })}
                  placeholder="Zip code"
                  required
                />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>Categories</h3>
            <FormField index={8}>
              <CustomSelect
                label="Categories"
                options={categories}
                onChange={(selectedOptions) => handleTagAdd('categories', selectedOptions)}
                value={supplier.categories}
                placeholder="Select categories"
                isMulti
              />
            </FormField>
          </FormSection>

          <ProductSection>
            <div className="product-header">
              <h3>Products</h3>
              <AddProductButton type='button' onClick={() => setIsModalOpen(true)}>
                <FaPlus /> Add Product
              </AddProductButton>
            </div>
            
            <ProductListDisplay
              products={supplier.products}
              onEdit={handleProductEdit}
              onDelete={handleProductDelete}
            />

            <ProductPriceModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={handleProductSave}
              products={products}
              editMode={editIndex !== null}
              existingProduct={editIndex !== null ? supplier.products[editIndex] : null}
            />
          </ProductSection>

          <FormSection>
            <h3>Additional Information</h3>
            <NotesField index={9}>
              <TextArea
                label="Notes"
                value={supplier.notes}
                onChange={(e) => setSupplier({ ...supplier, notes: e.target.value })}
                placeholder="Enter any additional notes"
                rows="4"
              />
            </NotesField>
          </FormSection>

          <SubmitButton type="submit">
            {loadingBtn ? (
              <>
                <FaSpinner className="icon-spin" />
                {id ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaSave />
                {id ? 'Update Supplier' : 'Create Supplier'}
              </>
            )}
          </SubmitButton>
        </form>
      </FormContainer>
    </DashboardLayout>
  );
};

export default SupplierForm;